// src/components/QuoteDialog.jsx
import { useEffect, useState } from 'react';
import { FaFilePdf, FaPrint, FaTimes, FaExternalLinkAlt, FaPlus, FaMinus, FaCopy } from 'react-icons/fa';
import printJS from 'print-js';
import html2pdf from 'html2pdf.js';
import axiosInstance from '../utils/axiosInstance';
import socket from '../utils/socket';
import { formatDate } from '../utils/formatDate';
import { formatDateTime } from '../utils/formatDateTime';
import VerifyDialog from '../utils/VerifyDialog';
import { QuoteStatusPanel } from './StatusControlPanel';


export default function QuoteDialog({ quote, onClose, onChanged, onDeleted }) {
    const [status, setStatus] = useState(quote.status);
    const [saving, setSaving] = useState(false);
    const [showQuoteForm, setShowQuoteForm] = useState(false);
    const [successMsg, setSuccessMsg] = useState('');
    const [confirmReject, setConfirmReject] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);




    const [quotes, setQuotes] = useState([
        {
            prices: { cake: '', decoration: '', filling: '', flavor: '' },
            expiryDate: '',
            note: '',
            cakeData: { size: '', shape: '', frosting: '', topper: '', additional: '' },
            options: {
                customerCanChooseFrosting: false,
                customerCanSeeAdditional: false,
                customerCanChooseTopper: false,
            },
        },
    ]);
    const includeAdditionalDetails = false;
    const [confirmSend, setConfirmSend] = useState(false);
    const [sending, setSending] = useState(false);
    const [copiedLinkId, setCopiedLinkId] = useState(null);
    // ── helpers for history UI ─────────────────────────────────────
    const fmtMoney = (v) =>
        Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    const fmtDateTime = (d) => new Date(d).toLocaleString();

    const statusBadgeClass = (status) => {
        switch ((status || '').toLowerCase()) {
            case 'accepted': return 'bg-green-100 text-green-700 border-green-200';
            case 'expired': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
            case 'revoked': return 'bg-red-100 text-red-700 border-red-200';
            case 'sent':
            case 'quoted':
            default: return 'bg-blue-100 text-blue-700 border-blue-200';
        }
    };


    const [history, setHistory] = useState([]); // previous quotes
    const [files, setFiles] = useState([]);     // all attachments (signed)

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const [h, f] = await Promise.all([
                    axiosInstance.get(`/api/quote-requests/${quote._id}/quotes`),
                    axiosInstance.get(`/api/quote-requests/${quote._id}/files-urls`)
                ]);
                if (!alive) return;
                setHistory(h.data?.quotes || []);
                setFiles(f.data?.files || []);
            } catch { }
        })();
        return () => { alive = false; };
    }, [quote._id]);

    useEffect(() => {
        const onQuoteViewed = ({ quoteRequestId, quoteMongoId, viewedAt }) => {
            if (quoteRequestId !== quote._id) return;
            setHistory((prev) =>
                prev.map((h) =>
                    h._id === quoteMongoId ? { ...h, viewedAt } : h
                )
            );
        };
        socket.on('quote:viewed', onQuoteViewed);
        return () => socket.off('quote:viewed', onQuoteViewed);
    }, [quote._id]);
    useEffect(() => {
        const onEsc = (e) => e.key === 'Escape' && onClose();
        window.addEventListener('keydown', onEsc);
        return () => window.removeEventListener('keydown', onEsc);
    }, [onClose]);

    const handlePrint = () => {
        printJS({ printable: 'quote-invoice', type: 'html', targetStyles: ['*'] });
    };

    const handleDownloadPDF = () => {
        const element = document.getElementById('quote-invoice');
        const opt = {
            margin: [0.5, 0.5, 0.5, 0.5],
            filename: `Quote_${quote._id}.pdf`,
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: { scale: 1.5, useCORS: true, scrollY: 0, scrollX: 0 },
            jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' },
            pagebreak: { mode: 'avoid-all' }
        };
        setTimeout(() => html2pdf().set(opt).from(element).save(), 100);
    };

    // ➕ Add these helper functions
    const addQuote = () => {
        setQuotes((prev) => [
            ...prev,
            {
                prices: { cake: '', decoration: '', filling: '', flavor: '' },
                expiryDate: '',
                note: '',
                cakeData: { size: '', shape: '', frosting: '', topper: '', additional: '' },
                options: {
                    customerCanChooseFrosting: false,
                    customerCanSeeAdditional: false,
                    customerCanChooseTopper: false,
                },
            },
        ]);
    };

    const removeQuote = (index) => {
        setQuotes((prev) => prev.filter((_, i) => i !== index));
    };

    const updateNestedField = (index, section, key, value) => {
        setQuotes((prev) =>
            prev.map((q, i) =>
                i === index ? { ...q, [section]: { ...q[section], [key]: value } } : q
            )
        );
    };

    const updateField = (index, key, value) => {
        setQuotes((prev) =>
            prev.map((q, i) => (i === index ? { ...q, [key]: value } : q))
        );
    };


    const updateStatus = async (newStatus) => {
        try {
            setSaving(true);
            const { data } = await axiosInstance.patch(`/api/quote-requests/${quote._id}/status`, { status: newStatus });
            setStatus(data.quote.status);
            onChanged?.(data.quote);
        } catch (e) {
            alert('Failed to update status');
        } finally {
            setSaving(false);
        }
    };

    const sendAllQuotes = async () => {
        if (!quotes?.length) {
            alert("Please add at least one quote before sending.");
            return;
        }

        const payload = {
            quotes: quotes.map((q) => {
                // 📅 Fix expiry date timezone shift
                let expiryIso;
                if (q.expiryDate) {
                    const localEndOfDay = new Date(`${q.expiryDate}T23:59:59`);
                    const offset = localEndOfDay.getTimezoneOffset() * 60000;
                    expiryIso = new Date(localEndOfDay.getTime() - offset).toISOString();
                } else {
                    // default: 7 days later (local)
                    const localDefault = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
                    const offset = localDefault.getTimezoneOffset() * 60000;
                    expiryIso = new Date(localDefault.getTime() - offset).toISOString();
                }

                return {
                    prices: {
                        cake: Number(q.prices.cake || 0),
                        decoration: Number(q.prices.decoration || 0),
                        filling: Number(q.prices.filling || 0),
                        flavor: Number(q.prices.flavor || 0),
                    },
                    note: q.note?.trim() || "",
                    expiryDate: expiryIso,
                    cakeData: {
                        size: q.cakeData.size || "",
                        shape: q.cakeData.shape || "",
                        frosting: q.cakeData.frosting || "",
                        topper: q.cakeData.topper || "",
                        additional: q.cakeData.additional || "",
                    },
                    includeAdditionalDetails,
                    options: {
                        customerCanChooseFrosting: !!q.options?.customerCanChooseFrosting,
                        customerCanSeeAdditional: !!q.options?.customerCanSeeAdditional,
                        customerCanChooseTopper: !!q.options?.customerCanChooseTopper,
                    },
                };
            }),
        };

        try {
            setSending(true);
            setSuccessMsg('');
            console.log("📤 Sending payload:", payload);

            await axiosInstance.post(
                `/api/quote-requests/${quote._id}/send-quote`,
                payload,
                { headers: { "Content-Type": "application/json" } }
            );

            // Refresh quotes
            const res = await axiosInstance.get(`/api/quote-requests/${quote._id}/quotes`);
            setHistory(res.data?.quotes || []);

            setSuccessMsg('✅ Quotes successfully emailed to customer!');
            onChanged?.({ ...quote, status: "quoted", latestQuoteSentAt: new Date().toISOString() });

        } catch (e) {
            console.error("❌ Send quotes failed:", e);
            alert(e?.response?.data?.message || "Failed to send quotes");
        } finally {
            setSending(false);
            setConfirmSend(false);
        }
    };


    const rejectQuote = async () => {
        try {
            setSending(true);
            setSuccessMsg('');
            await axiosInstance.post(`/api/quote-requests/${quote._id}/reject`);
            setSuccessMsg("❌ Quote request rejected and email sent to customer.");
            onChanged?.({ ...quote, status: "rejected" });
        } catch (e) {
            console.error("❌ Reject quote failed:", e);
            alert(e?.response?.data?.message || "Failed to reject quote.");
        } finally {
            setSending(false);
            setConfirmReject(false);
        }
    };



    const deleteQuote = async () => {
        try {
            await axiosInstance.delete(`/api/quote-requests/${quote._id}`);
            onDeleted?.(quote._id);
            window.location.reload();
        } catch (e) {
            alert('Failed to delete quote request.');
        } finally {
            setConfirmDelete(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
            <div
                onClick={(e) => e.stopPropagation()}
                className="bg-white w-full max-w-3xl mx-4 pt-0 p-6 rounded-lg shadow-lg overflow-y-auto overscroll-y-contain max-h-[90dvh] relative print:p-8 print:shadow-none"
            >
                {/* Header */}
                {/* Sticky Header */}
                <div className="sticky top-0 z-10 bg-white flex justify-between items-center mb-4 py-3 border-b print:hidden">
                    <h2 className="text-2xl font-bold">Quote Request</h2>
                    <div className="flex gap-2">
                        <button
                            className="flex items-center gap-2 bg-red-100 text-red-600 px-3 py-2 text-sm rounded hover:bg-red-200"
                            onClick={handleDownloadPDF}
                        >
                            <FaFilePdf /> PDF
                        </button>
                        <button
                            className="flex items-center gap-2 bg-blue-100 text-blue-600 px-3 py-2 text-sm rounded hover:bg-blue-200"
                            onClick={handlePrint}
                        >
                            <FaPrint /> Print
                        </button>
                        <button
                            className="flex items-center gap-2 bg-gray-100 text-gray-600 px-3 py-2 text-sm rounded hover:bg-gray-200"
                            onClick={onClose}
                        >
                            <FaTimes /> Close
                        </button>
                    </div>
                </div>


                {/* Printable content */}
                <div
                    id="quote-invoice"
                    className="text-black text-sm leading-relaxed w-full max-w-3xl mx-auto mt-6 p-4 space-y-6 bg-white rounded-lg"
                >
                    <div className="border-b pb-3 flex justify-between">
                        <div>
                            <h1 className="text-2xl font-bold text-candy-primary">Candy Haven and Cakes</h1>
                            <p className="text-xs text-gray-700">www.candyhavenandcakes.com</p>
                        </div>
                        <div className="text-right">
                            <h2 className="text-lg font-semibold uppercase">Quote</h2>
                            <p><strong>ID:</strong> {quote.quoteRequestId}</p>
                            <p><strong>Category:</strong> {quote.category}</p>
                            <p><strong>Event:</strong> {formatDate(quote.date)}</p>
                        </div>
                    </div>

                    {/* Compact two-column info section */}
                    <div
                        className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6"
                        style={{ breakInside: 'avoid' }} // helps prevent awkward page breaks when printing
                    >
                        {/* Customer */}
                        <section className="space-y-1">
                            <h3 className="text-lg font-semibold border-b pb-1 text-candy-primary">Customer</h3>
                            <p><strong>Name:</strong> {quote.firstName} {quote.lastName}</p>
                            <p><strong>Email:</strong> {quote.email}</p>
                            <p><strong>Phone:</strong> {quote.phone}</p>
                            <p><strong>Requested:</strong> {formatDateTime(new Date(new Date(quote.createdAt).setHours(new Date(quote.createdAt).getHours())))}</p>

                        </section>

                        {/* Cake Details from customer/request */}
                        <section className="space-y-1">
                            <h3 className="text-lg font-semibold border-b pb-1 text-candy-primary">Cake Details</h3>
                            <div className="grid grid-cols-1 sm:grid-cols-1 gap-x-4">
                                <p><strong>Servings:</strong> {quote.servings}</p>
                                <p>
                                    <strong>Flavor:</strong>{' '}
                                    {quote.flavor ?? quote.request?.flavor ?? '—'}
                                </p>
                                <p>
                                    <strong>Filling:</strong>{' '}
                                    {quote.filling ?? quote.request?.filling ?? '—'}
                                </p>
                            </div>
                            {quote.description && (
                                <p className="mt-1 whitespace-pre-line">
                                    <strong>Description:</strong> {quote.description}
                                </p>
                            )}
                        </section>

                    </div>

                    {/* Attachments stay below the two-column block */}
                    {files?.length > 0 && (
                        <div className="mt-3" style={{ breakInside: 'avoid' }}>
                            <strong>Attachments:</strong>
                            <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-3">
                                {files.map((f, idx) =>
                                    f.mimeType?.startsWith('image/') ? (
                                        <a key={idx} href={f.signedUrl} target="_blank" rel="noreferrer" className="block">
                                            <img
                                                src={f.signedUrl}
                                                alt={f.originalName}
                                                crossOrigin="anonymous"
                                                className="w-full max-h-40 object-contain rounded border"
                                            />
                                        </a>
                                    ) : (
                                        <a
                                            key={idx}
                                            href={f.signedUrl}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="text-candy-primary underline inline-flex items-center gap-1"
                                        >
                                            {f.originalName}
                                        </a>
                                    )
                                )}
                            </div>
                        </div>
                    )}


                    {/* 💬 Send / New Quote Section */}
                    <h3 className="text-lg font-semibold text-candy-primary mb-2">
                        {history.length > 0 ? "" : "Send Quote"}
                    </h3>

                    {/* If there are previous quotes → show New Quote button first */}
                    {history.length > 0 && !showQuoteForm ? (
                        <div className="flex justify-start mb-4">
                            <button
                                onClick={() => setShowQuoteForm(true)}
                                className="px-4 py-2 bg-candy-primary text-white rounded hover:bg-candy-primaryDark"
                            >
                                New Quote
                            </button>
                        </div>
                    ) : (
                        <>
                            {quotes.map((q, index) => {
                                return (
                                    <div
                                        key={index}
                                        className="relative rounded-lg border border-gray-200 bg-white shadow-sm p-4 mb-4"
                                    >
                                        {/* remove quote */}
                                        {quotes.length > 1 && (
                                            <button
                                                onClick={() => removeQuote(index)}
                                                className="absolute -top-2 -right-2 inline-flex h-8 w-8 items-center justify-center rounded-full bg-red-600 text-white shadow hover:bg-red-700"
                                                title="Remove this quote"
                                            >
                                                <FaMinus />
                                            </button>
                                        )}

                                        {/* header: title + expiry + total */}
                                        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                                            <h4 className="text-base font-semibold text-gray-800">Quote #{index + 1}</h4>
                                            <div className="flex items-center gap-3">
                                                <label className="text-xs text-gray-600">
                                                    Expiry
                                                    <input
                                                        type="date"
                                                        className="ml-2 rounded border px-2 py-1 text-sm"
                                                        value={q.expiryDate}
                                                        onChange={(e) => updateField(index, "expiryDate", e.target.value)}
                                                    />
                                                </label>
                                            </div>
                                        </div>
                                        {/* SPECS + PRICING (combined) */}
                                        <section className="rounded-lg border border-gray-100 bg-gray-50 p-3 mb-3">
                                            <div className="mb-2 text-sm font-semibold text-gray-700">Specs & Pricing</div>

                                            {/* line 1: size + shape */}
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                {[
                                                    ["size", "Size"],
                                                    ["shape", "Shape"],
                                                ].map(([k, label]) => (

                                                    <input
                                                        placeholder={label}
                                                        className="mt-1 w-full rounded border bg-white px-3 py-2 text-sm"
                                                        value={q.cakeData[k]}
                                                        onChange={(e) => updateNestedField(index, "cakeData", k, e.target.value)}
                                                    />
                                                ))}
                                            </div>

                                            {/* line 2: frosting + topper (each with toggle under) */}
                                            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                {/* Frosting */}
                                                <div>
                                                    <input
                                                        placeholder="Frosting"
                                                        className="mt-1 w-full rounded border bg-white px-3 py-2 text-sm"
                                                        value={q.cakeData.frosting}
                                                        onChange={(e) =>
                                                            updateNestedField(index, "cakeData", "frosting", e.target.value)
                                                        }
                                                    />
                                                    <label className="mt-2 inline-flex items-center gap-2 text-sm text-gray-700">
                                                        <input
                                                            type="checkbox"
                                                            checked={!!q.options?.customerCanChooseFrosting}
                                                            onChange={(e) =>
                                                                updateField(index, "options", {
                                                                    ...q.options,
                                                                    customerCanChooseFrosting: e.target.checked,
                                                                })
                                                            }
                                                        />
                                                        Let customer choose frosting
                                                    </label>
                                                </div>

                                                {/* Topper */}
                                                <div>

                                                    <input
                                                        placeholder="Topper"
                                                        className="mt-1 w-full rounded border bg-white px-3 py-2 text-sm"
                                                        value={q.cakeData.topper || ""}
                                                        onChange={(e) =>
                                                            updateNestedField(index, "cakeData", "topper", e.target.value)
                                                        }
                                                    />
                                                    <label className="mt-2 inline-flex items-center gap-2 text-sm text-gray-700">
                                                        <input
                                                            type="checkbox"
                                                            checked={!!q.options?.customerCanChooseTopper}
                                                            onChange={(e) =>
                                                                updateField(index, "options", {
                                                                    ...q.options,
                                                                    customerCanChooseTopper: e.target.checked,
                                                                })
                                                            }
                                                        />
                                                        Let customer choose topper
                                                    </label>
                                                </div>
                                            </div>

                                            {/* line 3: pricing + total */}
                                            <div className="mt-3 grid grid-cols-2 sm:grid-cols-5 gap-3 items-end">
                                                {[
                                                    ["cake", "Cake"],
                                                    ["decoration", "Decoration"],
                                                    ["filling", "Filling"],
                                                    ["flavor", "Flavor"],
                                                ].map(([k, label]) => (
                                                    <label key={k} className="text-xs font-medium text-gray-600">
                                                        {label}
                                                        <div className="mt-1 flex items-center rounded border bg-white pl-2">
                                                            <span className="text-gray-400">$</span>
                                                            <input
                                                                type="number"
                                                                inputMode="decimal"
                                                                step="0.01"
                                                                min="0"
                                                                className="w-full rounded-r px-2 py-1.5 text-sm outline-none"
                                                                value={q.prices[k]}
                                                                onChange={(e) =>
                                                                    updateNestedField(index, "prices", k, e.target.value)
                                                                }
                                                                placeholder="0.00"
                                                            />
                                                        </div>
                                                    </label>
                                                ))}

                                                {/* Total cell */}
                                                <div className="col-span-2 sm:col-span-1">
                                                    <div className="text-xs font-semibold text-gray-700 mb-1">Total</div>
                                                    <div className="rounded bg-candy-primary/10 px-3 py-2 text-sm font-semibold text-candy-primary text-center">
                                                        $
                                                        {(
                                                            Number(q.prices.cake || 0) +
                                                            Number(q.prices.decoration || 0) +
                                                            Number(q.prices.filling || 0) +
                                                            Number(q.prices.flavor || 0)
                                                        ).toFixed(2)}
                                                    </div>
                                                </div>
                                            </div>
                                        </section>

                                        {/* 3) EMAIL MESSAGE */}
                                        <section className="rounded-lg border border-gray-100 bg-gray-50 p-3 mb-3">
                                            <label className="text-xs font-semibold text-gray-700">
                                                Email message
                                                <input
                                                    className="mt-1 w-full rounded border px-3 py-2 text-sm bg-white"
                                                    value={q.note}
                                                    onChange={(e) => updateField(index, "note", e.target.value)}
                                                    placeholder="Short message for the customer (optional)"
                                                />
                                            </label>
                                        </section>

                                        {/* 4) ADDITIONAL NOTES */}
                                        <section className="rounded-lg border border-gray-100 bg-gray-50 p-3">
                                            <label className="text-xs font-semibold text-gray-700">
                                                Additional notes
                                                <textarea
                                                    rows={2}
                                                    className="mt-1 w-full rounded border bg-white px-3 py-2 text-sm"
                                                    placeholder="These notes will stay as internal notes unless you allow the customer to see them."
                                                    value={q.cakeData.additional}
                                                    onChange={(e) =>
                                                        updateNestedField(index, "cakeData", "additional", e.target.value)
                                                    }
                                                />
                                            </label>
                                            <label className="mt-2 inline-flex items-center gap-2 text-sm text-gray-700">
                                                <input
                                                    type="checkbox"
                                                    checked={!!q.options?.customerCanSeeAdditional}
                                                    onChange={(e) =>
                                                        updateField(index, "options", {
                                                            ...q.options,
                                                            customerCanSeeAdditional: e.target.checked,
                                                        })
                                                    }
                                                />
                                                Let customer see these notes
                                            </label>
                                        </section>
                                    </div>

                                );
                            })}

                            {/* Add another quote button */}
                            <button
                                onClick={addQuote}
                                className="flex items-center gap-2 text-candy-primary font-semibold mb-4"
                            >
                                <FaPlus /> Add Another Quote
                            </button>

                            <div className="text-right space-y-2">
                                {/* Show Send All Quotes always (for new + subsequent quotes) */}
                                {(history.length === 0 || showQuoteForm) && (
                                    <div className="flex justify-end gap-3">
                                        {/* Reject only if brand-new (no quote history) */}
                                        {history.length === 0 && (
                                            <button
                                                onClick={() => setConfirmReject(true)}
                                                disabled={sending || status === "rejected"}
                                                className={`px-4 py-2 rounded text-white ${status === "rejected"
                                                    ? "bg-gray-400 cursor-not-allowed"
                                                    : "bg-red-600 hover:bg-red-700"
                                                    }`}
                                            >
                                                {sending ? "Rejecting…" : status === "rejected" ? "Rejected" : "Reject"}
                                            </button>

                                        )}

                                        {/* Send All Quotes button - always available unless rejected */}
                                        <button
                                            onClick={() => setConfirmSend(true)}
                                            disabled={sending || status === "rejected"}
                                            className={`px-4 py-2 rounded text-white ${status === "rejected"
                                                ? "bg-gray-400 cursor-not-allowed"
                                                : "bg-green-600 hover:bg-green-700"
                                                }`}
                                        >
                                            {sending
                                                ? "Sending…"
                                                : quotes.length > 1
                                                    ? "Send All Quotes"
                                                    : "Send Quote"}
                                        </button>

                                    </div>
                                )}

                                {successMsg && (
                                    <p className="text-green-600 text-sm font-medium">{successMsg}</p>
                                )}
                            </div>




                        </>
                    )}


                    {/* ── History ──────────────────────────────────────────── */}
                    {/* ── History ──────────────────────────────────────────── */}
                    {history.length > 0 && (
                        <div className="mt-6 border-t pt-4">
                            <h4 className="text-sm font-semibold text-gray-800 mb-3">Previous Quotes</h4>

                            <div className="space-y-3">
                                {history.map((h) => {
                                    const p = h.prices || {};
                                    const total =
                                        Number(p.cake || 0) +
                                        Number(p.decoration || 0) +
                                        Number(p.filling || 0) +
                                        Number(p.flavor || 0);
                                    const isExpired = h.expiresAt && new Date(h.expiresAt) < new Date();
                                    const origin = process.env.REACT_APP_PUBLIC_APP_ORIGIN || window.location.origin;
                                    const quoteLink = h.token ? `${origin}/quote/${h.token}` : null;
                                    const quoteEmailLink = h.token ? `${origin}/quote/${h.token}?src=email` : null;
                                    return (
                                        <article
                                            key={h._id}
                                            className="rounded-lg border border-gray-200 bg-white/70 shadow-sm px-4 py-3"
                                        >
                                            {/* header */}
                                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                                                <div className="text-[13px] text-gray-600">
                                                    <span className="font-medium text-gray-800">Date:</span>{' '}
                                                    {fmtDateTime(h.createdAt)}{' '}
                                                    {h._id && (
                                                        <span className="text-gray-400">• Quote ID: {h.quoteId}</span>
                                                    )}
                                                </div>

                                                <div className="flex items-center gap-2">
                                                    <span
                                                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs border ${statusBadgeClass(
                                                            isExpired ? 'expired' : h.status
                                                        )}`}
                                                        title={h.status}
                                                    >
                                                        {isExpired ? 'expired' : (h.status || 'sent')}
                                                    </span>

                                                    <div className="text-sm">
                                                        <span className="text-gray-500 mr-1 font-medium">Total:</span>
                                                        <span className="text-candy-primary font-bold">
                                                            ${fmtMoney(total)}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* expiry row */}
                                            {(h.expiresAt || h.note || h.viewedAt || h.status === 'sent') && (
                                                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                                                    {h.expiresAt && (
                                                        <div className="text-[13px] text-gray-600">
                                                            <span className="font-medium text-gray-800">Expires:</span>{' '}
                                                            {fmtDateTime(h.expiresAt)}
                                                        </div>
                                                    )}
                                                    <div className="text-[13px] text-gray-600">
                                                        <span className="font-medium text-gray-800">Customer viewed:</span>{' '}
                                                        {h.viewedAt ? (
                                                            <span className="text-green-700 font-medium">{fmtDateTime(h.viewedAt)}</span>
                                                        ) : (
                                                            <span className="text-gray-400 italic">Not yet</span>
                                                        )}
                                                    </div>
                                                    {h.note && (
                                                        <div className="text-[13px] text-gray-600">
                                                            <span className="font-medium text-gray-800">Email Message:</span>{' '}
                                                            <span className="italic">{h.note}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {/* price grid */}
                                            <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
                                                <div className="rounded border border-gray-200 bg-gray-50 px-3 py-2 text-center">
                                                    <div className="text-[11px] uppercase tracking-wide text-gray-500">Cake</div>
                                                    <div className="text-sm font-semibold">${fmtMoney(p.cake)}</div>
                                                </div>
                                                <div className="rounded border border-gray-200 bg-gray-50 px-3 py-2 text-center">
                                                    <div className="text-[11px] uppercase tracking-wide text-gray-500">Decoration</div>
                                                    <div className="text-sm font-semibold">${fmtMoney(p.decoration)}</div>
                                                </div>
                                                <div className="rounded border border-gray-200 bg-gray-50 px-3 py-2 text-center">
                                                    <div className="text-[11px] uppercase tracking-wide text-gray-500">Filling</div>
                                                    <div className="text-sm font-semibold">${fmtMoney(p.filling)}</div>
                                                </div>
                                                <div className="rounded border border-gray-200 bg-gray-50 px-3 py-2 text-center">
                                                    <div className="text-[11px] uppercase tracking-wide text-gray-500">Flavor</div>
                                                    <div className="text-sm font-semibold">${fmtMoney(p.flavor)}</div>
                                                </div>
                                            </div>

                                            {/* specs */}
                                            {h.cakeData && (h.cakeData.size || h.cakeData.shape || h.cakeData.frosting || h.cakeData.additional) && (
                                                <div className="mt-3">
                                                    <div className="text-[13px] font-semibold text-gray-800 mb-1">Proposed Cake Specs</div>
                                                    <div className="flex flex-wrap gap-2">
                                                        {h.cakeData.size && (
                                                            <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-1 text-[12px] text-gray-700 border border-gray-200">
                                                                Size: {h.cakeData.size}
                                                            </span>
                                                        )}
                                                        {h.cakeData.shape && (
                                                            <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-1 text-[12px] text-gray-700 border border-gray-200">
                                                                Shape: {h.cakeData.shape}
                                                            </span>
                                                        )}
                                                        {h.cakeData.frosting && (
                                                            <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-1 text-[12px] text-gray-700 border border-gray-200">
                                                                Frosting: {h.cakeData.frosting}
                                                            </span>
                                                        )}
                                                        {h.cakeData.additional && (
                                                            <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-1 text-[12px] text-gray-700 border border-gray-200">
                                                                Notes: {h.cakeData.additional}
                                                            </span>
                                                        )}
                                                        {h.cakeData.topper && (
                                                            <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-1 text-[12px] text-gray-700 border border-gray-200">
                                                                Topper: {h.cakeData.topper}
                                                            </span>
                                                        )}


                                                    </div>
                                                </div>
                                            )}

                                            {quoteLink && (
                                                <div className="mt-3 flex flex-wrap gap-2 items-center">
                                                    <button
                                                        type="button"
                                                        onClick={() => window.open(quoteLink, "_blank")}
                                                        className="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded bg-candy-primary text-white hover:bg-candy-primaryDark"
                                                    >
                                                        <FaExternalLinkAlt /> View Quote Link
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={async () => {
                                                            try {
                                                                await navigator.clipboard.writeText(quoteEmailLink);
                                                                setCopiedLinkId(h._id);
                                                                setTimeout(() => setCopiedLinkId(null), 2000);
                                                            } catch {
                                                                setSuccessMsg('Could not copy link');
                                                                setTimeout(() => setSuccessMsg(''), 3000);
                                                            }
                                                        }}
                                                        className="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                                                    >
                                                        <FaCopy />
                                                        {copiedLinkId === h._id ? 'Copied!' : 'Copy Link'}
                                                    </button>
                                                </div>
                                            )}
                                        </article>
                                    );
                                })}
                            </div>
                        </div>
                    )}


                    <div className="pt-4 mt-2 border-t">
                        <QuoteStatusPanel
                            status={status}
                            onStatusChange={updateStatus}
                            onDelete={() => setConfirmDelete(true)}
                            canDelete={status === 'rejected'}
                            busy={saving}
                        />
                        <div className="hidden print:block font-semibold capitalize mt-2">Status: {status}</div>
                    </div>
                </div>
            </div>

            {/* Confirm Send Dialog */}
            <VerifyDialog
                open={confirmSend}
                title="Send Quotes"
                message="Are you sure you want to send all quotes to the customer?"
                onConfirm={() => {
                    setConfirmSend(false);    // ✅ CLOSE IMMEDIATELY
                    sendAllQuotes();          // ✅ THEN send the request
                }}
                onCancel={() => setConfirmSend(false)}
            />
            {/* Reject Confirmation */}
            <VerifyDialog
                open={confirmReject}
                title="Reject Quote Request"
                warning="This action will mark the quote as rejected and notify the customer by email."
                message="Are you sure you want to reject this quote request? This cannot be undone."
                onConfirm={rejectQuote}
                onCancel={() => setConfirmReject(false)}
            />

            {/* Delete Confirmation */}
            <VerifyDialog
                open={confirmDelete}
                title="Delete Quote Request"
                warning="This action is permanent. All quote data will be lost."
                message="Are you sure you want to permanently delete this quote request?"
                onConfirm={deleteQuote}
                onCancel={() => setConfirmDelete(false)}
            />



        </div>
    );
}
